import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { useUser } from '/imports/hooks';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { PageFooter } from '/imports/ui/PageFooter';
import { Form } from '/imports/ui/Form';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { toastOnError } from '/imports/ui/utils';
import {
  createAlsoEnergyCredential, getAlsoEnergyCredentialsForOrg,
  AlsoEnergyCredential, pullAlsoEnergySites
} from '/imports/api/alsoEnergyMeters';
import { Loading } from '/imports/ui/Loading';

const AlsoEnergyMeterPageStyled = styled(Page)`
  .result {
    margin: 2rem 0;
    .buttons {
      display: flex;
      gap: 1rem;
      margin-top: 1rem;
    }
  }
  table {
    td {
      padding: 1rem;
      &:first-child {
        padding-left: 0;
      }
    }
  }
  textarea {
    height: 14rem;
  }
  #import-file-input {
    display: none;
  }
`;

/** Super-user page to add AlsoEnergy credentials and pull their meter systems. */
export const AlsoEnergyMeterPage = () => {
  const user = useUser();
  if (!user?.isSuperUser) return <NotPermittedPage />;
  const orgId = user.orgId;
  const [username, setUsername] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [credentials, setAlsoEnergyCredentials] = useState<AlsoEnergyCredential[]>([]);
  useEffect(() => {
    (async () => {
      if (!orgId) return;
      setAlsoEnergyCredentials(await getAlsoEnergyCredentialsForOrg.call(orgId));
    })();
  }, [orgId]);
  const [pullingForTokens, setPullingForTokens] = useState<string[]>([]);
  const pullHardware = toastOnError(async (tokenId: string) => {
    setPullingForTokens([...pullingForTokens, tokenId]);
    const { numSystems, numCreated } = await pullAlsoEnergySites.call(tokenId);
    toast.success(`${numSystems} ${strings.SystemsFound()} | ${numCreated} ${strings.Created()}`);
    setPullingForTokens(pullingForTokens.filter((t) => t !== tokenId));
  });
  const save = toastOnError(async () => {
    if (!username || !password || !orgId) return;
    await createAlsoEnergyCredential.call({ username, password });
    setAlsoEnergyCredentials(await getAlsoEnergyCredentialsForOrg.call(orgId));
  });
  return (
    <AlsoEnergyMeterPageStyled className="page">
      <PageHeading
        left={<h1>AlsoEnergy</h1>}
      />
      <PageBody>
        <Form>
          <div className="field-group">
            <FormField field="username">
              <label>{strings.Username()}</label>
              <Input value={username}
                onChange={(username) => setUsername(username)} />
            </FormField>
            <FormField field="password">
              <label>{strings.Password()}</label>
              <Input value={password} type="password"
                onChange={(password) => setPassword(password)} />
            </FormField>
          </div>
          <div><Button onClick={() => save()}>{strings.Save()}</Button></div>
        </Form>
        {
          credentials.length ?
            <div>
              <h2>{strings.Accounts()}</h2>
              <table>
                <tbody>
                  {
                    credentials.map((t) => (
                      <tr key={t._id}>
                        <td>{t.username}</td>
                        <td>
                          {
                            pullingForTokens.includes(t._id) ? 
                              <Button><Loading /></Button>
                              :
                              <Button onClick={() => pullHardware(t._id)}>{strings.PullSystems()}</Button>
                          }
                        </td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
            :
            null
        }
      </PageBody>
      <PageFooter
      />
    </AlsoEnergyMeterPageStyled>
  );
};
